import * as React from 'react';
import get from 'lodash/get';

import { Modal, Text, Button, Heading3 } from '@origin-digital/style-guide';
import { navigation, task } from '@origin-digital/reporting-client';

import { Address } from './Steps/Address';
import { EnergyType } from './Steps/EnergyType';
import { Renewables } from './Steps/Renewables';
import { Review } from './Steps/Review';
import { PlanType } from './Steps/PlanType';
import { RewardType } from './Steps/RewardType';

import { Wizard } from '../Wizard';
import { IWizardConfig } from '../Wizard/Wizard.types';
import {
  AddressStepValidation,
  EnergyTypeVadidation,
  RenewableValidation,
  PlanTypeValidation,
  RewardTypeValidation
} from './validations';
import { BENEFITS } from './constants';
import {
  HMCContainer,
  Prompt,
  PromptContainer,
  StyledModal,
  Disclaimer,
} from './HelpMeChoose.styles';
import { planTitle } from './utils';

const NON_REWARD_TYPE_CHOSEN = (answers) => answers.planType !== BENEFITS;

interface IHelpMeChooseState {
  showModal: boolean;
  recommendedPlan?: object;
}

interface IHelpMeChooseProps {
  addressApiProps: object;
  addressReverseLookupApiEndPoint: string;
  plansApiEndPoint: string;
  answers: object;
  showModal?: boolean;
  onSelectPlan: (plan) => any;
  onComplete: (answers) => any;
}

export class HelpMeChoose extends React.Component<IHelpMeChooseProps, IHelpMeChooseState> {
  public state = { showModal: !!this.props.showModal };

  public config() {
    const {
      addressApiProps,
      addressReverseLookupApiEndPoint,
      plansApiEndPoint,
      onSelectPlan,
    } = this.props;

    const hmcConfig: IWizardConfig = {
      steps: [
        {
          component: Address,
          name: 'Address',
          props: {
            addressApiProps,
            addressReverseLookupApiEndPoint,
            plansApiEndPoint,
          },
          validationSchema: AddressStepValidation,
        },
        {
          component: EnergyType,
          name: 'Energy Type',
          props: {
            plansApiEndPoint,
          },
          validationSchema: EnergyTypeVadidation,
        },
        {
          component: Renewables,
          name: 'Renewables',
          validationSchema: RenewableValidation,
        },
        {
          component: PlanType,
          name: 'Plan Type',
          validationSchema: PlanTypeValidation,
        },
        {
          component: RewardType,
          disabled: NON_REWARD_TYPE_CHOSEN,
          name: 'Rewards Type',
          validationSchema: RewardTypeValidation
        },
        {
          component: Review,
          name: 'Review',
          props: {
            addressApiProps,
            plansApiEndPoint,
            onSelectPlan,
            onComparePlans: this.hideModal,
            onComplete: this.handleComplete,
          },
        },
      ],
    };

    return hmcConfig;
  }

  showRecommendedPlan() {
    const plans = get(this.props.answers, 'plans') || [];
    const { recommendedPlan } = this.state;
    if (!recommendedPlan) {
      return false;
    }

    return plans.some((p) => p.title === recommendedPlan.title);
  }

  handleComplete = (answers) => {
    const { onComplete } = this.props;
    this.setState({ recommendedPlan: answers.recommendedPlan });
    onComplete(answers);
  };

  public render() {
    const { showModal, recommendedPlan } = this.state;
    const showRecommendedPlan = this.showRecommendedPlan();
    return (
      <>
        <PromptContainer>
          <Prompt>
            {showRecommendedPlan ? (
              <div className="recommended">
                <Heading3
                  marginBottom={false}
                  text={
                    <>
                      Your recommended plan was <span>{planTitle(recommendedPlan)}</span>
                    </>
                  }
                />
              </div>
            ) : (
              <div>
                <Heading3
                  text="Find the best plan for you by answering a few short questions"
                  marginBottom={false}
                />
              </div>
            )}
            <Button
              data-id="help-me-choose"
              text={showRecommendedPlan ? 'Start over' : 'Get started'}
              onClick={this.showModal}
            />
          </Prompt>
        </PromptContainer>
        {showModal && (
          <StyledModal>
            <Modal width="100vw" onDismiss={this.hideModal}>
              <HMCContainer>
                <Wizard config={this.config()} initialStep={1} answers={this.props.answers} />
                <Disclaimer>
                  <Text color="grey80" size="xs" align="center">
                    We care about your privacy. We don’t store or share any of the details you give
                    us here.
                  </Text>
                </Disclaimer>
              </HMCContainer>
            </Modal>
          </StyledModal>
        )}
      </>
    );
  }

  private showModal = () => {
    this.setState({ showModal: true });
    task.base('cp-wizard-start', {
      action: 'start',
    });
  };

  private hideModal = () => {
    this.setState({ showModal: false });
    navigation('screen', {
      currentUri: window.location.href,
      friendlyUri: `originenergy:electricity-gas:plans`,
    });
  };
}
